import { BootstrapBreakpoint } from '@/constants/BootstrapBreakpoint';
import { createGlobalState } from '@vueuse/shared';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';

export const useScreenSizes = createGlobalState(
  (): {
    scrollbarWidth: Ref<number | null>;
    doesScrollbarTakeUpSpace: ComputedRef<boolean>;
    screenSize: Ref<BootstrapBreakpoint>;
    millimeterRulerHeight: Ref<number>;
    convertPxToMm: (px: number) => number;
    convertMmToPx: (mm: number) => number;
    isSmallScreen: ComputedRef<boolean>;
    isLargeScreen: ComputedRef<boolean>;
  } => {
    const scrollbarWidth = ref<number | null>(null);

    const doesScrollbarTakeUpSpace = computed(() => (scrollbarWidth.value ?? 0) > 0);

    const screenSize = ref(BootstrapBreakpoint.XS);

    const isSmallScreen = computed(() => screenSize.value < BootstrapBreakpoint.LG);
    const isLargeScreen = computed(() => screenSize.value >= BootstrapBreakpoint.LG);

    /**
     * Sane first value taken from https://www.unitconverters.net/typography/pixel-x-to-millimeter.htm
     *
     * Adjusted automatically on platform page load to be completely accurate.
     */
    const millimeterRulerHeight = ref(26.4583);

    const convertPxToMm = (px: number): number => Math.floor(px / (millimeterRulerHeight.value / 100));
    const convertMmToPx = (mm: number): number => Math.floor(mm * (millimeterRulerHeight.value / 100));

    return {
      scrollbarWidth,
      doesScrollbarTakeUpSpace,
      screenSize,
      millimeterRulerHeight,
      convertPxToMm,
      convertMmToPx,
      isSmallScreen,
      isLargeScreen,
    };
  },
);

/**
 * Assumes a base font size of 16px
 */
export const convertRemToPx = (rawRem: string): number => {
  // strip the 'rem' from the string
  return parseFloat(rawRem) * 16;
};

/**
 * Measures `ch` (the width of "0") in `fontFamily` at the page font size, for APIs that only accept pixels.
 * Widths in `ch` stay wide enough for their content when a white-label swaps the font family.
 */
export const convertChToPx = (ch: number, fontFamily: string): number => {
  const probe = document.createElement('div');
  probe.style.cssText = `position: absolute; visibility: hidden; width: ${ch}ch; font-family: ${fontFamily}`;
  document.body.appendChild(probe);
  const widthPx = probe.getBoundingClientRect().width;
  probe.remove();

  return widthPx;
};
